/**
 * Custom tracking utilities
 *
 * Client-side tracking helpers that complement the server-side tracking
 * system (inc/tracking/). Add custom tracking functions here as needed.
 *
 * Current features:
 * - Query parameter forwarding to app domain links (UTMs, per-page configured
 *   params via window.felixForwardParams, and the fsid session handoff)
 * - Event tracking via data-track-* attributes (GTM dataLayer + Segment)
 *
 * @package Felix
 */

// =============================================================================
// Session
// =============================================================================

/**
 * Read the current Felix session id.
 *
 * @returns {string|null} Session id, or null when the session script is absent
 */
const getSessionId = () => {
	try {
		return window.felixSession?.get() || null
	} catch {
		return null
	}
}

// =============================================================================
// Query Parameter Forwarding (UTM + per-page configured params + session)
// =============================================================================

// Onboarding-app hosts that receive forwarded params. Add future hosts here.
const APP_HOSTS = ['my.felixforyou.ca', 'lucky.felixforyou.ca']

// Cheap pre-filter for the page-load sweep, so the CSS engine rejects the bulk
// of a page's anchors before any of them reach isAppLink(). Substring matching
// only ever over-matches here — everything it returns still faces the hostname
// check. Matches case-sensitively, which is fine: the sweep only sees markup we
// author.
const APP_LINK_SELECTOR = APP_HOSTS.map((host) => `a[href*="${host}"]`).join(',')
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

// Session id handed to the app in the URL — the cookie is host-only by design,
// so the receiver cannot read it. Bare uuid, nothing else: the receiver dates
// the session against its own record, not against anything transported.
const SESSION_PARAM = 'fsid'

/**
 * Get UTM parameters from current URL
 * @param {URLSearchParams} urlParams - Parsed current URL query string
 * @returns {Map<string, string>} Map of UTM parameter names to values
 */
const getUtmParams = (urlParams) => {
	const values = new Map()

	UTM_PARAMS.forEach((param) => {
		const value = urlParams.get(param)
		if (value) {
			values.set(param, value)
		}
	})

	return values
}

/**
 * Get per-page configured forward params (window.felixForwardParams).
 *
 * Set server-side on partner pages as an array of { param, value }: a non-empty
 * value is always appended (static); a blank value forwards that param's value
 * from the incoming URL (pass-through).
 *
 * @param {URLSearchParams} urlParams - Parsed current URL query string
 * @returns {Map<string, string>} Map of parameter names to resolved values
 */
const getConfiguredParams = (urlParams) => {
	const values = new Map()
	const config = window.felixForwardParams

	if (!Array.isArray(config)) return values

	config.forEach((entry) => {
		const param = (entry?.param || '').trim()
		if (!param) return

		const staticValue = entry?.value || ''
		if (staticValue) {
			values.set(param, staticValue)
			return
		}

		const incoming = urlParams.get(param)
		if (incoming) {
			values.set(param, incoming)
		}
	})

	return values
}

/**
 * Whether a link targets an onboarding-app host.
 *
 * @param {Element} link - Anchor element
 * @returns {boolean} True when the link goes to an app host
 */
const isAppLink = (link) => {
	const href = link.getAttribute('href')
	if (!href) return false

	try {
		const { hostname } = new URL(href, window.location.href)
		if (hostname === window.location.hostname) return false

		return APP_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
	} catch {
		return false
	}
}

/**
 * Append parameters to a URL (only when the URL doesn't already set them).
 *
 * Uses string concatenation rather than URLSearchParams.set() so the original
 * query string is preserved byte-for-byte — mutating searchParams re-serializes
 * the whole query (form-urlencoded), which corrupts existing params such as
 * `redirect=/apply/wl` (→ %2Fapply%2Fwl) or signature params.
 *
 * @param {string} href - Original URL
 * @param {Map<string, string>} paramValues - Parameters to append
 * @returns {string} URL with parameters appended
 */
const appendParamsToUrl = (href, paramValues) => {
	// Split off any fragment so appended params land in the query string.
	const hashIndex = href.indexOf('#')
	const base = hashIndex === -1 ? href : href.slice(0, hashIndex)
	const hash = hashIndex === -1 ? '' : href.slice(hashIndex)

	const queryIndex = base.indexOf('?')
	const query = queryIndex === -1 ? '' : base.slice(queryIndex + 1)
	const existing = new URLSearchParams(query)

	const additions = []
	paramValues.forEach((value, param) => {
		if (!existing.has(param)) {
			additions.push(`${encodeURIComponent(param)}=${encodeURIComponent(value)}`)
		}
	})

	if (additions.length === 0) return href

	const sep = queryIndex === -1 ? '?' : query === '' ? '' : '&'
	return base + sep + additions.join('&') + hash
}

/**
 * Decorate one link, if it targets an app host.
 *
 * The HTMLAnchorElement guard is for SVG <a>, which also matches the selector
 * but whose href is an SVGAnimatedString rather than a writable string.
 *
 * @param {Element} link - Anchor element
 * @param {Map<string, string>} paramValues - Parameters to append
 */
const decorateAppLink = (link, paramValues) => {
	if (!(link instanceof HTMLAnchorElement)) return
	if (!isAppLink(link)) return

	link.href = appendParamsToUrl(link.href, paramValues)
}

/**
 * Decorate every app link in the document.
 *
 * @param {Map<string, string>} paramValues - Parameters to append
 */
const processAppLinks = (paramValues) => {
	if (paramValues.size === 0) return

	document.querySelectorAll(APP_LINK_SELECTOR).forEach((link) => {
		decorateAppLink(link, paramValues)
	})
}

/**
 * Initialize query parameter forwarding to app-domain links.
 * Forwards UTM params, per-page configured params (which win over UTMs on key
 * collisions) and the session id.
 *
 * Runs once, at page load: a link's fsid is whatever the session was then, so a
 * click after a 30-minute idle carries an id whose session has since rolled over.
 * Accepted deliberately — the receiver cannot tell, since the param carries no
 * timestamp. Links added to the DOM later, and links inside a shadow root, are
 * not decorated at all.
 */
const initParamForwarding = () => {
	const urlParams = new URLSearchParams(window.location.search)

	const paramValues = getUtmParams(urlParams)
	getConfiguredParams(urlParams).forEach((value, param) => {
		paramValues.set(param, value)
	})

	const sessionId = getSessionId()
	if (sessionId) {
		paramValues.set(SESSION_PARAM, sessionId)
	}

	processAppLinks(paramValues)
}

// =============================================================================
// Event Tracking (GTM dataLayer + Segment)
// =============================================================================

/**
 * Convert kebab-case to camelCase
 * @param {string} str - Kebab-case string
 * @returns {string} camelCase string
 */
const toCamelCase = (str) => str.replace(/-([a-z])/g, (g) => g[1].toUpperCase())

/**
 * Get page context from body data attribute
 * Cached on first call for performance
 * @returns {string|null} Page name or null if not set
 */
const getPageContext = (() => {
	let cached = null
	let hasCached = false

	return () => {
		if (!hasCached) {
			// felixPageName is inlined before this bundle by felix_enqueue_scripts().
			// The body-dataset read stays as the fallback: it is what this function
			// has always looked for, so anything setting it keeps working.
			cached = window.felixPageName || document.body?.dataset?.pageName || null
			hasCached = true
		}
		return cached
	}
})()

/**
 * Extract tracking properties from an element's data-track-* attributes.
 * Converts attribute names to camelCase (e.g. data-track-link-name -> linkName).
 *
 * @param {HTMLElement} element - Element with data-track-* attributes
 * @returns {{ event: string, props: Object }|null} Event name and properties, or null
 */
const extractTrackingData = (element) => {
	const event = element.dataset.trackEvent
	if (!event) return null

	const props = {}

	Object.keys(element.dataset).forEach((key) => {
		if (key.startsWith('track') && key !== 'trackEvent') {
			const paramName = toCamelCase(
				key.replace('track', '').charAt(0).toLowerCase() + key.replace('track', '').slice(1)
			)
			props[paramName] = element.dataset[key]
		}
	})

	// Add page context if available and not already set.
	const pageContext = getPageContext()
	if (pageContext && !props.pageName) {
		props.pageName = pageContext
	}

	return { event, props }
}

// Longest a caller will be made to wait for the tags on an event it is about to
// navigate away from. Reached only when a destination accepted the event and then
// stalled; the usual path hands back in tens of milliseconds.
const SEND_TIMEOUT = 500

/**
 * Push an event to GTM dataLayer and Segment analytics.
 * Both destinations receive the same event name and properties.
 *
 * @param {string} event - Event name
 * @param {Object} props - Event properties
 * @param {Function} [onSent] - Called once the tags have run, or on timeout.
 *                              Supply it only when about to leave the page:
 *                              GTM evaluates tags asynchronously, so a click
 *                              that navigates immediately sends nothing at all.
 */
const dispatchTrackingEvent = (event, props = {}, onSent) => {
	if (!event) return

	const sessionId = getSessionId()
	const payload = sessionId ? { ...props, felixSessionId: sessionId } : props

	const hasGtm = Boolean(window.google_tag_manager)
	const hasSegment = Boolean(window.analytics && typeof window.analytics.track === 'function')

	// Segment's snippet defines track() inline as a queue long before the library
	// arrives, and that placeholder never runs a callback. So it is always worth
	// calling, and only worth waiting for once the real library is in.
	const segmentWillReport = Boolean(hasSegment && window.analytics.initialized)
	const willReport = Boolean(hasGtm || segmentWillReport)

	// Whichever destination reports first releases the caller, and only once.
	let released = false
	const release = () => {
		if (released || !onSent) return
		released = true
		onSent()
	}

	// Armed before the pushes, not after: a caller that cancelled a navigation to
	// wait for this must still be released if a push throws.
	if (onSent && willReport) {
		window.setTimeout(release, SEND_TIMEOUT)
	}

	try {
		// GTM dataLayer. eventCallback is GTM's own contract for this, and it is
		// ignored by anything that is not a container, so it costs nothing.
		window.dataLayer = window.dataLayer || []
		window.dataLayer.push(
			onSent && hasGtm
				? { event, ...payload, eventCallback: release, eventTimeout: SEND_TIMEOUT }
				: { event, ...payload }
		)

		// Segment analytics.
		if (hasSegment) {
			window.analytics.track(event, payload, {}, segmentWillReport && onSent ? release : undefined)
		}
	} catch {
		// Reporting is never worth stranding someone mid-click for.
	}

	// Nothing can report back, so there is nothing to wait for.
	if (onSent && !willReport) {
		release()
	}
}

/**
 * Push event to GTM dataLayer and Segment analytics.
 * Both destinations receive the same event name and properties.
 *
 * @param {HTMLElement} element - Element with data-track-* attributes
 */
const pushTrackingEvent = (element) => {
	const data = extractTrackingData(element)
	if (!data) return

	dispatchTrackingEvent(data.event, data.props)
}

/**
 * Initialize event tracking listeners.
 * Uses event delegation for performance and dynamic elements.
 */
const initTrackingEvents = () => {
	document.addEventListener('click', (e) => {
		const trackElement = e.target.closest('[data-track-event]')
		if (trackElement) {
			pushTrackingEvent(trackElement)
		}
	})
}

// =============================================================================
// Main Export
// =============================================================================

/**
 * Initialize all custom tracking utilities
 */
export function initTracking() {
	initParamForwarding()
	initTrackingEvents()

	// Programmatic events for components that have no click to hang data-track-*
	// attributes on — e.g. a calculator reporting that a result was produced.
	// Same two destinations, same shape, and the page context is added for both.
	// A third argument waits for the tags before running, for a caller that is
	// about to navigate away.
	window.felixTrack = (event, props = {}, onSent) => {
		const pageContext = getPageContext()
		dispatchTrackingEvent(event, pageContext && !props.pageName ? { ...props, pageName: pageContext } : props, onSent)
	}
}
